import { LittleModalCreateEdit } from '../../../../../../baseComponents/ModalsLayouts/ModalCreateEdit'
import { FormRender } from '../../../../../../forms/FormRenderer/types'
import useTwinTranslation from '../../../../../../utils/hooks/useTwinTranslation'
import { PdfTemplateModelType, PdfTemplatePdfTypesTy } from '@teinor/erp/types/company/config/pdfTemplate'
import { ModalEditComponentProps } from '../../../../../../baseComponents/ModalsLayouts/types'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { getActiveLangs } from '../../../../../../utils/reducers/getters'
import usePdfTemplateTypes from '../../../../../../utils/hooks/pdf/usePdfTemplateTypes'
import { TwinDictionary } from '../../../../../../utils/globals/dictionary'
import allTemplates from '../../../../../../utils/hooks/pdf/templates'
import LoadingSpinner from '../../../../../../baseComponents/LoaderDecider/LoadingSpinner'
import { valueOrDefaultValue } from '../../../../../../utils/globals/valueOrDefaultValue'
import { permissionCheck } from '../../../../../../baseComponents/PermissionChecker/function'
import { PdfTemplatePayload } from '../../../../../../utils/reducers/company/pdfTemplates'
import { ConnectedProps, connect } from 'react-redux'
import withLoading from '../../../../../../utils/hoc/withLoading'
import { AllReduxPayloads } from '../../../../../../utils/reducers'

interface ModalCEPdfTemplateProps extends ModalCEPdfTemplateLogicProps { }

const ModalCEPdfTemplate: React.FC<ModalCEPdfTemplateProps> = ({ ...rest }) => {
    const langs = useMemo(() => getActiveLangs(), [])
    const { t } = useTwinTranslation()
    const { pdfTemplateTypes } = usePdfTemplateTypes()
    const { myType, setMyType, templateSectionItems, getCodeTemplate, setMyTemplate, myOwnTemplates } = useModalCEPdfTemplateLogic({...rest})

    const fields: FormRender<PdfTemplateModelType> = [
        {
            cols: 1,
            elements: [
                {
                    name: 'name',
                    label: t('name', 'Nombre'),
                    component: 'InputWithLabelMargin',
                    required: true
                },
                {
                    name: 'LangId',
                    label: t('language', 'Idioma'),
                    component: 'CustomSelectWithSearchBar',
                    items: langs,
                    required: true
                },
                {
                    name: 'type',
                    label: t('type', 'Tipo'),
                    component: 'CustomSelect',
                    items: pdfTemplateTypes,
                    required: true,
                    onChange: (value) => setMyType(value),
                    value: myType || ''
                },
            ]
        },
    ]

    if (myType) {
        if (!rest.allRowData) {
            if (myOwnTemplates) {
                fields[0].elements.push(
                    {
                        name: 'template',
                        component: 'CustomSelectSectionsSearchbar',
                        items: templateSectionItems,
                        label: t('templates', 'Plantillas'),
                        onChange: (value) => setMyTemplate(value)
                    },
                    {
                        name: 'code',
                        component: 'InputHidden',
                        value: getCodeTemplate()
                    }
                )
            } else {
                fields[0].elements.push(
                    {
                        name: 'template',
                        component: 'Custom',
                        render: () => <LoadingSpinner className='w-30 h-30 m-auto mt-20' />
                    },
                )
            }
        }
    }
    fields[0].elements.push(
        {
            name: 'is_main',
            label: t('default', 'Por defecto'),
            component: 'CheckboxMargin',
        },
        {
            name: 'id',
            component: 'InputHidden',
        }
    )

    if (rest.allRowData && rest.userPermissions) {
        const parsedFields = valueOrDefaultValue(fields, rest.userPermissions, rest.allRowData)
        return (<LittleModalCreateEdit fields={parsedFields} url={'/api/app/config/pdfTemplate/updatePdfTemplate'} translations={{ title: t('editDocument', 'Editar documento'), button: t('save', 'Guardar') }} haveButtonPermissions={permissionCheck(rest.userPermissions, 'update')} {...rest} />)
    }

    return (<LittleModalCreateEdit fields={fields} url={'/api/app/config/pdfTemplate/createPdfTemplate'} translations={{
        title: t('createDocument', 'Crear documento'),
        button: t('create', 'Crear')
    }} haveButtonPermissions={true} {...rest} />)
}

type ModalCEPdfTemplateLogicProps = ModalEditComponentProps & ReduxModalCEPdfTemplate & { }

const useModalCEPdfTemplateLogic = ({ allRowData, pdfTemplates}: ModalCEPdfTemplateLogicProps) => {
    const [myType, setMyType] = useState<keyof PdfTemplatePdfTypesTy | null>(allRowData?.type || null)
    const [myTemplate, setMyTemplate] = useState<string | null>(null)
    const { t } = useTwinTranslation()
    const [myOwnTemplates, setMyOwnTemplates] = useState<TwinDictionary | null>(null)
    const teinorTemplates = useMemo(() => { return { name: t('teinorTemplates', 'Plantillas Teinor'), childs: myType ? allTemplates[myType] : {}} }, [t, myType])
    const base = { name: t('withoutTemplate', 'Sin plantilla'), childs: { '': { name: '---', value: '' } } }
    const templateSectionItems = [
        teinorTemplates,
        { name: t('ownTemplates', 'Plantillas propias'), childs: myOwnTemplates || {} },
        base
    ]
    const getMyTemplates = useCallback(async () => {
        if (myType) {
            setMyOwnTemplates(null)
            if (pdfTemplates && pdfTemplates[myType]) {
                const myTemplates: TwinDictionary = {}
                for (const key in pdfTemplates[myType]) {
                    const element = pdfTemplates[myType][key]
                    myTemplates[element.id] = { name: element.name, code: element.code }
                }
                setMyOwnTemplates(myTemplates)
            }
        }
    }, [myType, pdfTemplates])

    const getCodeTemplate = useCallback(() => {
        if (myTemplate && myType) {
            if (myTemplate.includes('teinor')) {
                return JSON.stringify(allTemplates[myType][myTemplate].code)
            } else {
                return myOwnTemplates?.[myTemplate].code
            }
        }
        return null
    }, [myType, myTemplate, myOwnTemplates])

    useEffect(() => {
        getMyTemplates()
    }, [getMyTemplates])
    
    return { myType, setMyType, templateSectionItems, getCodeTemplate, setMyTemplate, myOwnTemplates }
}

const pdfTemplateDispatch = {
    setPdfTemplates: (payload: PdfTemplatePayload) => ({ type: 'CHANGE_PDFTEMPLATE', payload }),
}

export type ReduxModalCEPdfTemplate = ConnectedProps<typeof modalCEPdfTemplateConnect>
const mapModalCEPdfTemplateConnector = (state: AllReduxPayloads) => ({ pdfTemplates: state.pdfTemplates })
const modalCEPdfTemplateConnect = connect(mapModalCEPdfTemplateConnector, pdfTemplateDispatch)

const modalCEPdfTemplateConnectLoading = withLoading(ModalCEPdfTemplate, [{ fetchUrl: '/api/app/config/pdfTemplate/getAllPdfTemplates', propName: 'pdfTemplates', setFunctionName: 'setPdfTemplates' }])

const ModalCEPdfTemplateConnect = modalCEPdfTemplateConnect(modalCEPdfTemplateConnectLoading)

export default ModalCEPdfTemplateConnect